import type { SyncLocal } from "@roqeet/core";
import type { SyncManifest, SyncRecord } from "@roqeet/core";
import type { Entity } from "@roqeet/core";
import type { Table } from "dexie";
import { getDB, type RoqeetDB } from "../db/schema.js";

/**
 * 로컬 저장소 측 동기화 어댑터 — 결정 #20. core의 `runSync`가 호출하는 {@link SyncLocal}
 * 계약을 Dexie/IndexedDB 위에 구현한다.
 *
 * 핵심 불변식(매우 중요): `applyRecords`는 원격 레코드를 **raw로 그대로 기록**해야
 * 한다. 일반 Repository.put은 매 쓰기마다 sync 메타(rev/hash)를 재계산(bump)하는데,
 * 동기화로 받은 레코드는 원격의 rev/hash를 **보존**해야 한다 — 아니면 다음 사이클에서
 * 자기 자신이 "변경됨"으로 보여 가짜 충돌(false conflict)이 무한 발생한다. 그래서
 * Dexie 테이블에 직접 put한다(메타 보존).
 *
 * 매니페스트는 tombstone(소프트 삭제)까지 포함해 열거한다 — 삭제 전파(결정 #20)를
 * 위해서다. 베이스 매니페스트(3-way 기준)는 localStorage에 영속한다.
 */

/** 동기화 대상 테이블(설정·메시지 포함). 각 행은 sync 메타를 가진 Entity다. */
const SYNC_TABLES = [
  "characters",
  "personas",
  "chats",
  "lorebooks",
  "presets",
  "scripts",
  "providerProfiles",
  "generatedImages",
  "assets",
  "messages",
  "gameCards",
] as const;

type SyncTableName = (typeof SYNC_TABLES)[number];

const BASE_MANIFEST_KEY = "roqeet.sync.base";

/** sync 메타를 가진 행(모든 동기화 대상 엔티티의 공통 형태). */
type SyncRow = Entity;

export class WebSyncLocal implements SyncLocal {
  private readonly db: RoqeetDB = getDB();

  /** 테이블명 → Dexie 테이블 핸들. */
  private table(name: string): Table<SyncRow, string> | undefined {
    if ((SYNC_TABLES as readonly string[]).includes(name)) {
      return this.db.table<SyncRow, string>(name);
    }
    return undefined;
  }

  /** Entity → SyncRecord(전송 형태). */
  private toRecord(table: SyncTableName, row: SyncRow): SyncRecord {
    return {
      table,
      id: row.id,
      hash: row.sync.hash ?? "",
      rev: row.sync.rev,
      ...(row.sync.deletedAt ? { deleted: true } : {}),
      payload: row,
    };
  }

  async localManifest(): Promise<SyncManifest> {
    const manifest: SyncManifest = {};
    for (const name of SYNC_TABLES) {
      const rows = await this.db.table<SyncRow, string>(name).toArray();
      for (const row of rows) {
        manifest[row.id] = {
          hash: row.sync.hash ?? "",
          rev: row.sync.rev,
          ...(row.sync.deletedAt ? { deleted: true } : {}),
        };
      }
    }
    return manifest;
  }

  async baseManifest(): Promise<SyncManifest> {
    try {
      const raw = localStorage.getItem(BASE_MANIFEST_KEY);
      return raw ? (JSON.parse(raw) as SyncManifest) : {};
    } catch {
      return {};
    }
  }

  async saveBaseManifest(manifest: SyncManifest): Promise<void> {
    try {
      localStorage.setItem(BASE_MANIFEST_KEY, JSON.stringify(manifest));
    } catch {
      // private mode / quota: 베이스 미저장 시 다음 사이클이 첫 동기화처럼 보수적으로
      // 동작(충돌로 올림) — 데이터 손실은 없다(결정 #20).
    }
  }

  async getRecords(ids: string[]): Promise<SyncRecord[]> {
    const wanted = new Set(ids);
    const out: SyncRecord[] = [];
    // id는 앱 전역 UUID라 어느 테이블에 있는지 모른다 → 테이블별 bulkGet으로 탐색.
    for (const name of SYNC_TABLES) {
      if (wanted.size === 0) break;
      const remaining = [...wanted];
      const rows = await this.db.table<SyncRow, string>(name).bulkGet(remaining);
      rows.forEach((row, i) => {
        if (row) {
          out.push(this.toRecord(name, row));
          wanted.delete(remaining[i]!);
        }
      });
    }
    return out;
  }

  async applyRecords(records: SyncRecord[]): Promise<void> {
    // 테이블별로 묶어 raw bulkPut(메타 보존 — 절대 재bump 금지).
    const byTable = new Map<string, SyncRow[]>();
    for (const rec of records) {
      const payload = rec.payload as SyncRow | null;
      if (!payload || typeof payload !== "object") continue;
      if (!this.table(rec.table)) continue; // 모르는 테이블은 무시(전방 호환)
      const bucket = byTable.get(rec.table);
      if (bucket) bucket.push(payload);
      else byTable.set(rec.table, [payload]);
    }
    for (const [name, rows] of byTable) {
      await this.db.table<SyncRow, string>(name).bulkPut(rows);
    }
  }

  // ---- 에셋 바이트(콘텐츠 주소화, 결정 #18) ----

  async hasBlob(hash: string): Promise<boolean> {
    return (await this.db.assetBlobs.get(hash)) !== undefined;
  }

  async getBlob(hash: string): Promise<Blob | undefined> {
    const row = await this.db.assetBlobs.get(hash);
    return row?.data;
  }

  async putBlob(hash: string, blob: Blob): Promise<void> {
    const existing = await this.db.assetBlobs.get(hash);
    if (!existing) await this.db.assetBlobs.put({ hash, data: blob });
  }
}
